import React from "react";
import { FiAlertTriangle, FiPackage, FiCheckCircle } from "react-icons/fi";
import { BsArrowRightShort } from "react-icons/bs";

const StockAlert = () => {
  // Static data for demonstration
  const stockAlerts = [
    {
      id: 1,
      productName: "Premium Cotton T-Shirt",
      sku: "TSH-001-BLK",
      currentStock: 3,
      threshold: 10,
      status: "critical",
      lastUpdated: "2 hours ago",
    },
    {
      id: 2,
      productName: "Slim Fit Jeans",
      sku: "JNS-002-BLU",
      currentStock: 5,
      threshold: 15,
      status: "warning",
      lastUpdated: "5 hours ago",
    },
    {
      id: 3,
      productName: "Running Shoes",
      sku: "SHO-003-WHT",
      currentStock: 12,
      threshold: 20,
      status: "normal",
      lastUpdated: "1 day ago",
    },
    {
      id: 4,
      productName: "Baseball Cap",
      sku: "CAP-004-RED",
      currentStock: 2,
      threshold: 5,
      status: "critical",
      lastUpdated: "3 hours ago",
    },
    {
      id: 5,
      productName: "Sports Socks",
      sku: "SOC-005-BLK",
      currentStock: 8,
      threshold: 25,
      status: "warning",
      lastUpdated: "6 hours ago",
    },
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case "critical":
        return (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            <FiAlertTriangle className="mr-1" /> Critical
          </span>
        );
      case "warning":
        return (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
            <FiAlertTriangle className="mr-1" /> Warning
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            <FiCheckCircle className="mr-1" /> Normal
          </span>
        );
    }
  };

  const getProgressBarColor = (status) => {
    switch (status) {
      case "critical":
        return "bg-red-500";
      case "warning":
        return "bg-yellow-500";
      default:
        return "bg-green-500";
    }
  };

  const calculatePercentage = (current, threshold) => {
    return Math.min(100, (current / threshold) * 100);
  };

  return (
    <div className="container mx-auto min-h-screen px-4 py-8 ">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stock Alerts</h1>
          <p className="mt-1 text-gray-600">
            Monitor your inventory levels and get notified when stock runs low
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button className="rounded-md bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700">
            Set Alert Preferences
          </button>
        </div>
      </div>
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium  uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium  uppercase tracking-wider">
                  Stock Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium  uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium  uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium  uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {stockAlerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
                        <FiPackage className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {alert.productName}
                        </div>
                        <div className="text-sm text-gray-500">{alert.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center">
                      <div className="mr-4 w-full">
                        <div className="mb-1 flex justify-between text-sm text-gray-600">
                          <span>{alert.currentStock} in stock</span>
                          <span>Threshold: {alert.threshold}</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-200">
                          <div
                            className={`h-2 rounded-full ${getProgressBarColor(
                              alert.status
                            )}`}
                            style={{
                              width: `${calculatePercentage(
                                alert.currentStock,
                                alert.threshold
                              )}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {getStatusBadge(alert.status)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {alert.lastUpdated}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                    <button className="flex items-center text-indigo-600 hover:text-indigo-900">
                      Restock <BsArrowRightShort className="ml-1" size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="rounded-full bg-red-100 p-3 text-red-600">
              <FiAlertTriangle size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Critical Alerts
              </h3>
              <p className="text-2xl font-bold text-red-600">2 Products</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="rounded-full bg-yellow-100 p-3 text-yellow-600">
              <FiAlertTriangle size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Warning Alerts
              </h3>
              <p className="text-2xl font-bold text-yellow-600">2 Products</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="rounded-full bg-green-100 p-3 text-green-600">
              <FiCheckCircle size={24} />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Normal Stock
              </h3>
              <p className="text-2xl font-bold text-green-600">1 Product</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-md border-l-4 border-blue-400 bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Inventory Tips
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  Critical alerts indicate stock levels below 20% of your
                  threshold
                </li>
                <li>
                  Warning alerts indicate stock levels between 20-50% of your
                  threshold
                </li>
                <li>Update your stock thresholds in the alert preferences</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAlert;
